import dayjs from 'dayjs';

import { getUpdatedFieldKeys, SelectorMap } from './compare-obj-functions';
import { formatGymDate } from './date-functions';

/**
 * Returns the list of class edit field keys that changed between initial and updated states.
 * Tracks changes to: class_type, coach, capacity, location, duration, date, start_time, waitlist_limit.
 */
export const getChangedKeysForClassEdit = (initial: any, updated: any): string[] => {
  try {
    const selectors: SelectorMap = {
      class_type: o => o?.kiloClassType?.kiloClassTypeId,
      coach: o =>
        (o?.classCoachClassCoachRoles || [])
          .map((r: any) => `${r?.coach?.staffId}:${r?.classCoachRole?.kiloClassCoachRoleId}`)
          .sort(),
      capacity: o => o?.capacity,
      location: o => o?.location?.locationId,
      duration: o => o?.duration,
      date: o => dayjs(o?.startDate).format('YYYY-MM-DD'),
      start_time: o => o?.startTime,
      waitlist_limit: o => o?.waitlistLimit,
    };
    return getUpdatedFieldKeys(initial, updated, selectors);
  } catch {
    return [];
  }
};

/**
 * Returns the list of appointment edit field keys that changed between initial and updated states.
 * Tracks changes to: appointment_type, coach, location, duration, date, start_time.
 */
export const getChangedKeysForAppointmentEdit = (initial: any, updated: any): string[] => {
  try {
    const selectors: SelectorMap = {
      appointment_type: o => o?.appointmentType?.appointmentTypeId,
      coach: o =>
        (o?.appointmentCoachAppointmentCoachRoles || [])
          .map((r: any) => `${r?.coach?.staffId}:${r?.appointmentCoachRole?.appointmentCoachRoleId}`)
          .sort(),
      location: o => o?.location?.locationId,
      duration: o => o?.duration,
      date: o => dayjs(o?.startDate).format('YYYY-MM-DD'),
      start_time: o => o?.startTime,
    };
    return getUpdatedFieldKeys(initial, updated, selectors);
  } catch {
    return [];
  }
};

/**
 * Returns the list of athlete edit field keys that changed between initial and updated states.
 * Tracks changes to: firstName, lastName, email, phone, dob, gender, memberSince, address fields, emergencyName, emergencyPhone, medicalNotes, disableSmsNotifications.
 */
export const getChangedKeysForAthleteEdit = (initial: any, updated: any, timezone?: string): string[] => {
  try {
    const selectors: SelectorMap = {
      first_name: o => o?.firstName,
      last_name: o => o?.lastName,
      email: o => o?.email,
      phone: o => o?.phone,
      dob: o => (o?.dob ? dayjs(o.dob).format('YYYY-MM-DD') : null),
      gender: o => o?.gender,
      // ENG-2344: normalize to the gym-local date so the initial (stored string) and updated
      // (form Date) sides match when unchanged, keeping the ENG-867 edit-omit correct.
      member_since: o => (o?.memberSince ? formatGymDate(o.memberSince, timezone, 'YYYY-MM-DD') : null),
      address_lines: o => o?.address?.addressLines,
      city: o => o?.address?.locality,
      state: o => o?.address?.region,
      zip_code: o => o?.address?.zipCode,
      country: o => o?.address?.country,
      emergency_name: o => o?.emergencyName,
      emergency_phone: o => o?.emergencyPhone,
      medical_notes: o => o?.medicalNotes,
      disable_sms_notifications: o => o?.disableSmsNotifications,
    };
    return getUpdatedFieldKeys(initial, updated, selectors);
  } catch {
    return [];
  }
};

/**
 * Returns the list of pay rate edit field keys that changed between initial and updated states.
 * Tracks changes to: name, pay_rate_type, rate, percentage, additional_per_attendee, include_no_shows,
 * include_late_cancels, pay_rate_tiers.
 */
export const getChangedKeysForPayRateEdit = (initial: any, updated: any): string[] => {
  try {
    const selectors: SelectorMap = {
      name: o => o?.name,
      pay_rate_type: o => o?.payRateType,
      rate: o => o?.rate,
      percentage: o => o?.percentage,
      additional_per_attendee: o => o?.additionalPerAttendee,
      include_no_shows: o => o?.payForNoShow,
      include_late_cancels: o => o?.payForLateCancel,
      pay_rate_tiers: o => o?.payRateTiers,
    };
    return getUpdatedFieldKeys(initial, updated, selectors);
  } catch {
    return [];
  }
};
